import React, { useState, useEffect } from 'react'
import { Search, Bell, TrendingUp, Users, Globe, FileText, Bookmark, Filter } from 'lucide-react'
import toast from 'react-hot-toast'
import GrantCard from '../components/GrantCard'
import FeatureCard from '../components/FeatureCard'
import { grantsData } from '../utils/grantsData'

const Home = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedType, setSelectedType] = useState('all')
  const [selectedAudience, setSelectedAudience] = useState('all')
  const [minAmount, setMinAmount] = useState('')
  const [maxAmount, setMaxAmount] = useState('')
  const [savedGrants, setSavedGrants] = useState([])
  const [filteredGrants, setFilteredGrants] = useState(grantsData)

  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem('savedGrants') || '[]')
    setSavedGrants(saved)
  }, [])

  useEffect(() => {
    let filtered = grantsData

    if (searchTerm) {
      filtered = filtered.filter(grant =>
        grant.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        grant.organization.toLowerCase().includes(searchTerm.toLowerCase()) ||
        grant.description.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    if (selectedType !== 'all') {
      filtered = filtered.filter(grant => grant.type === selectedType)
    }

    if (selectedAudience !== 'all') {
      filtered = filtered.filter(grant => grant.targetAudience.includes(selectedAudience))
    }

    if (minAmount) {
      filtered = filtered.filter(grant => grant.amount >= parseInt(minAmount))
    }

    if (maxAmount) {
      filtered = filtered.filter(grant => grant.amount <= parseInt(maxAmount))
    }

    setFilteredGrants(filtered)
  }, [searchTerm, selectedType, selectedAudience, minAmount, maxAmount])

  const handleSaveGrant = (grantId) => {
    const isAlreadySaved = savedGrants.includes(grantId)
    let newSavedGrants

    if (isAlreadySaved) {
      newSavedGrants = savedGrants.filter(id => id !== grantId)
      toast.success('Grant removed from saved list')
    } else {
      newSavedGrants = [...savedGrants, grantId]
      toast.success('Grant saved successfully!')
    }

    setSavedGrants(newSavedGrants)
    localStorage.setItem('savedGrants', JSON.stringify(newSavedGrants))
  }

  const features = [
    {
      icon: Search,
      title: 'Searchable Database',
      description: 'Access thousands of grants across climate, education, research, arts, and business funding'
    },
    {
      icon: Bell,
      title: 'Custom Alerts',
      description: 'Get notified when new grants matching your criteria become available'
    },
    {
      icon: TrendingUp,
      title: 'Analytics Dashboard',
      description: 'Track your application success rates and funding trends over time'
    },
    {
      icon: Users,
      title: 'Team Collaboration',
      description: 'Work together with your team on grant applications and proposals'
    },
    {
      icon: FileText,
      title: 'Proposal Writing',
      description: 'Access templates and tools to craft compelling grant proposals'
    },
    {
      icon: Bookmark,
      title: 'Saved Lists',
      description: 'Organize and track grants you\'re interested in applying for'
    }
  ]

  return (
    <div className="min-h-screen" data-component-path="client/src/pages/Home.jsx" data-component-line="106:5" data-component-id="client/src/pages/Home.jsx:106:5">
      <section className="relative bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 py-20 overflow-hidden" data-component-path="client/src/pages/Home.jsx" data-component-line="107:7" data-component-id="client/src/pages/Home.jsx:107:7">
        <div className="absolute inset-0 bg-[url('https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=1920')] bg-cover bg-center opacity-10" data-component-path="client/src/pages/Home.jsx" data-component-line="108:9" data-component-id="client/src/pages/Home.jsx:108:9"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-component-path="client/src/pages/Home.jsx" data-component-line="110:9" data-component-id="client/src/pages/Home.jsx:110:9">
          <div className="text-center animate-fade-in" data-component-path="client/src/pages/Home.jsx" data-component-line="111:11" data-component-id="client/src/pages/Home.jsx:111:11">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6" data-component-path="client/src/pages/Home.jsx" data-component-line="112:13" data-component-id="client/src/pages/Home.jsx:112:13">
              Discover Grants
              <span className="block bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent" data-component-path="client/src/pages/Home.jsx" data-component-line="114:15" data-component-id="client/src/pages/Home.jsx:114:15">
                That Fund Your Vision
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto" data-component-path="client/src/pages/Home.jsx" data-component-line="118:13" data-component-id="client/src/pages/Home.jsx:118:13">
              Track global funding opportunities for climate action, research, education, arts, and sustainable development
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center" data-component-path="client/src/pages/Home.jsx" data-component-line="121:13" data-component-id="client/src/pages/Home.jsx:121:13">
              <button className="flex items-center justify-center gap-2 h-14 px-8 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:shadow-xl transition-all duration-200 text-lg font-semibold" data-component-path="client/src/pages/Home.jsx" data-component-line="122:15" data-component-id="client/src/pages/Home.jsx:122:15">
                <Search className="w-6 h-6" data-component-path="client/src/pages/Home.jsx" data-component-line="123:17" data-component-id="client/src/pages/Home.jsx:123:17" />
                <span data-component-path="client/src/pages/Home.jsx" data-component-line="124:17" data-component-id="client/src/pages/Home.jsx:124:17">Start Searching</span>
              </button>
              <button className="flex items-center justify-center gap-2 h-14 px-8 bg-white text-gray-900 rounded-lg hover:shadow-lg transition-all duration-200 text-lg font-semibold border-2 border-gray-200" data-component-path="client/src/pages/Home.jsx" data-component-line="126:15" data-component-id="client/src/pages/Home.jsx:126:15">
                <Globe className="w-6 h-6" data-component-path="client/src/pages/Home.jsx" data-component-line="127:17" data-component-id="client/src/pages/Home.jsx:127:17" />
                <span data-component-path="client/src/pages/Home.jsx" data-component-line="128:17" data-component-id="client/src/pages/Home.jsx:128:17">Explore Global Grants</span>
              </button>
            </div>
          </div>

          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto" data-component-path="client/src/pages/Home.jsx" data-component-line="133:11" data-component-id="client/src/pages/Home.jsx:133:11">
            <div className="bg-white rounded-xl p-6 shadow-lg text-center" data-component-path="client/src/pages/Home.jsx" data-component-line="134:13" data-component-id="client/src/pages/Home.jsx:134:13">
              <div className="text-3xl font-bold text-blue-600 mb-2" data-component-path="client/src/pages/Home.jsx" data-component-line="135:15" data-component-id="client/src/pages/Home.jsx:135:15">10K+</div>
              <div className="text-gray-600 text-sm" data-component-path="client/src/pages/Home.jsx" data-component-line="136:15" data-component-id="client/src/pages/Home.jsx:136:15">Active Grants</div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-lg text-center" data-component-path="client/src/pages/Home.jsx" data-component-line="138:13" data-component-id="client/src/pages/Home.jsx:138:13">
              <div className="text-3xl font-bold text-purple-600 mb-2" data-component-path="client/src/pages/Home.jsx" data-component-line="139:15" data-component-id="client/src/pages/Home.jsx:139:15">150+</div>
              <div className="text-gray-600 text-sm" data-component-path="client/src/pages/Home.jsx" data-component-line="140:15" data-component-id="client/src/pages/Home.jsx:140:15">Countries</div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-lg text-center" data-component-path="client/src/pages/Home.jsx" data-component-line="142:13" data-component-id="client/src/pages/Home.jsx:142:13">
              <div className="text-3xl font-bold text-pink-600 mb-2" data-component-path="client/src/pages/Home.jsx" data-component-line="143:15" data-component-id="client/src/pages/Home.jsx:143:15">$5B+</div>
              <div className="text-gray-600 text-sm" data-component-path="client/src/pages/Home.jsx" data-component-line="144:15" data-component-id="client/src/pages/Home.jsx:144:15">Total Funding</div>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-lg text-center" data-component-path="client/src/pages/Home.jsx" data-component-line="146:13" data-component-id="client/src/pages/Home.jsx:146:13">
              <div className="text-3xl font-bold text-green-600 mb-2" data-component-path="client/src/pages/Home.jsx" data-component-line="147:15" data-component-id="client/src/pages/Home.jsx:147:15">50K+</div>
              <div className="text-gray-600 text-sm" data-component-path="client/src/pages/Home.jsx" data-component-line="148:15" data-component-id="client/src/pages/Home.jsx:148:15">Users</div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white" data-component-path="client/src/pages/Home.jsx" data-component-line="154:7" data-component-id="client/src/pages/Home.jsx:154:7">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-component-path="client/src/pages/Home.jsx" data-component-line="155:9" data-component-id="client/src/pages/Home.jsx:155:9">
          <div className="text-center mb-12" data-component-path="client/src/pages/Home.jsx" data-component-line="156:11" data-component-id="client/src/pages/Home.jsx:156:11">
            <h2 className="text-4xl font-bold text-gray-900 mb-4" data-component-path="client/src/pages/Home.jsx" data-component-line="157:13" data-component-id="client/src/pages/Home.jsx:157:13">Powerful Features</h2>
            <p className="text-xl text-gray-600" data-component-path="client/src/pages/Home.jsx" data-component-line="158:13" data-component-id="client/src/pages/Home.jsx:158:13">Everything you need to find and win grants</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-component-path="client/src/pages/Home.jsx" data-component-line="161:11" data-component-id="client/src/pages/Home.jsx:161:11">
            {features.map((feature, index) => (
              <FeatureCard key={index} {...feature} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50" data-component-path="client/src/pages/Home.jsx" data-component-line="169:7" data-component-id="client/src/pages/Home.jsx:169:7">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-component-path="client/src/pages/Home.jsx" data-component-line="170:9" data-component-id="client/src/pages/Home.jsx:170:9">
          <div className="text-center mb-12" data-component-path="client/src/pages/Home.jsx" data-component-line="171:11" data-component-id="client/src/pages/Home.jsx:171:11">
            <h2 className="text-4xl font-bold text-gray-900 mb-4" data-component-path="client/src/pages/Home.jsx" data-component-line="172:13" data-component-id="client/src/pages/Home.jsx:172:13">Search Grants</h2>
            <p className="text-xl text-gray-600" data-component-path="client/src/pages/Home.jsx" data-component-line="173:13" data-component-id="client/src/pages/Home.jsx:173:13">Find funding opportunities tailored to your needs</p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 mb-8" data-component-path="client/src/pages/Home.jsx" data-component-line="176:11" data-component-id="client/src/pages/Home.jsx:176:11">
            <div className="flex items-center gap-4 mb-6" data-component-path="client/src/pages/Home.jsx" data-component-line="177:13" data-component-id="client/src/pages/Home.jsx:177:13">
              <div className="flex-1 relative" data-component-path="client/src/pages/Home.jsx" data-component-line="178:15" data-component-id="client/src/pages/Home.jsx:178:15">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" data-component-path="client/src/pages/Home.jsx" data-component-line="179:17" data-component-id="client/src/pages/Home.jsx:179:17" />
                <input
                  type="text"
                  placeholder="Search grants by title, organization, or keyword..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-colors"
                  data-component-path="client/src/pages/Home.jsx" data-component-line="180:17" data-component-id="client/src/pages/Home.jsx:180:17"
                />
              </div>
              <button className="flex items-center justify-center gap-2 h-11 px-6 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" data-component-path="client/src/pages/Home.jsx" data-component-line="188:15" data-component-id="client/src/pages/Home.jsx:188:15">
                <Filter className="w-5 h-5" data-component-path="client/src/pages/Home.jsx" data-component-line="189:17" data-component-id="client/src/pages/Home.jsx:189:17" />
                <span data-component-path="client/src/pages/Home.jsx" data-component-line="190:17" data-component-id="client/src/pages/Home.jsx:190:17">Filters</span>
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-component-path="client/src/pages/Home.jsx" data-component-line="194:13" data-component-id="client/src/pages/Home.jsx:194:13">
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                data-component-path="client/src/pages/Home.jsx" data-component-line="195:15" data-component-id="client/src/pages/Home.jsx:195:15"
              >
                <option value="all" data-component-path="client/src/pages/Home.jsx" data-component-line="200:17" data-component-id="client/src/pages/Home.jsx:200:17">All Types</option>
                <option value="climate" data-component-path="client/src/pages/Home.jsx" data-component-line="201:17" data-component-id="client/src/pages/Home.jsx:201:17">Climate</option>
                <option value="education" data-component-path="client/src/pages/Home.jsx" data-component-line="202:17" data-component-id="client/src/pages/Home.jsx:202:17">Education</option>
                <option value="research" data-component-path="client/src/pages/Home.jsx" data-component-line="203:17" data-component-id="client/src/pages/Home.jsx:203:17">Research</option>
                <option value="arts" data-component-path="client/src/pages/Home.jsx" data-component-line="204:17" data-component-id="client/src/pages/Home.jsx:204:17">Arts</option>
                <option value="business" data-component-path="client/src/pages/Home.jsx" data-component-line="205:17" data-component-id="client/src/pages/Home.jsx:205:17">Business</option>
                <option value="sustainable-development" data-component-path="client/src/pages/Home.jsx" data-component-line="206:17" data-component-id="client/src/pages/Home.jsx:206:17">Sustainable Development</option>
              </select>

              <select
                value={selectedAudience}
                onChange={(e) => setSelectedAudience(e.target.value)}
                className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                data-component-path="client/src/pages/Home.jsx" data-component-line="209:15" data-component-id="client/src/pages/Home.jsx:209:15"
              >
                <option value="all" data-component-path="client/src/pages/Home.jsx" data-component-line="214:17" data-component-id="client/src/pages/Home.jsx:214:17">All Audiences</option>
                <option value="nonprofits" data-component-path="client/src/pages/Home.jsx" data-component-line="215:17" data-component-id="client/src/pages/Home.jsx:215:17">Nonprofits</option>
                <option value="researchers" data-component-path="client/src/pages/Home.jsx" data-component-line="216:17" data-component-id="client/src/pages/Home.jsx:216:17">Researchers</option>
                <option value="students" data-component-path="client/src/pages/Home.jsx" data-component-line="217:17" data-component-id="client/src/pages/Home.jsx:217:17">Students</option>
                <option value="individuals" data-component-path="client/src/pages/Home.jsx" data-component-line="218:17" data-component-id="client/src/pages/Home.jsx:218:17">Individuals</option>
                <option value="social-enterprises" data-component-path="client/src/pages/Home.jsx" data-component-line="219:17" data-component-id="client/src/pages/Home.jsx:219:17">Social Enterprises</option>
                <option value="startups" data-component-path="client/src/pages/Home.jsx" data-component-line="220:17" data-component-id="client/src/pages/Home.jsx:220:17">Startups</option>
              </select>

              <input
                type="number"
                placeholder="Min Amount ($)"
                value={minAmount}
                onChange={(e) => setMinAmount(e.target.value)}
                className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                data-component-path="client/src/pages/Home.jsx" data-component-line="223:15" data-component-id="client/src/pages/Home.jsx:223:15"
              />

              <input
                type="number"
                placeholder="Max Amount ($)"
                value={maxAmount}
                onChange={(e) => setMaxAmount(e.target.value)}
                className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                data-component-path="client/src/pages/Home.jsx" data-component-line="231:15" data-component-id="client/src/pages/Home.jsx:231:15"
              />
            </div>
          </div>

          <div className="mb-6 flex items-center justify-between" data-component-path="client/src/pages/Home.jsx" data-component-line="240:11" data-component-id="client/src/pages/Home.jsx:240:11">
            <p className="text-gray-600" data-component-path="client/src/pages/Home.jsx" data-component-line="241:13" data-component-id="client/src/pages/Home.jsx:241:13">
              Found <span className="font-semibold text-blue-600" data-component-path="client/src/pages/Home.jsx" data-component-line="242:21" data-component-id="client/src/pages/Home.jsx:242:21">{filteredGrants.length}</span> grants
            </p>
            {savedGrants.length > 0 && (
              <p className="text-gray-600" data-component-path="client/src/pages/Home.jsx" data-component-line="245:15" data-component-id="client/src/pages/Home.jsx:245:15">
                <span className="font-semibold text-purple-600" data-component-path="client/src/pages/Home.jsx" data-component-line="246:17" data-component-id="client/src/pages/Home.jsx:246:17">{savedGrants.length}</span> saved
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-component-path="client/src/pages/Home.jsx" data-component-line="251:11" data-component-id="client/src/pages/Home.jsx:251:11">
            {filteredGrants.map((grant) => (
              <GrantCard
                key={grant.id}
                grant={grant}
                isSaved={savedGrants.includes(grant.id)}
                onSave={handleSaveGrant}
              />
            ))}
          </div>

          {filteredGrants.length === 0 && (
            <div className="text-center py-12" data-component-path="client/src/pages/Home.jsx" data-component-line="263:13" data-component-id="client/src/pages/Home.jsx:263:13">
              <p className="text-xl text-gray-500" data-component-path="client/src/pages/Home.jsx" data-component-line="264:15" data-component-id="client/src/pages/Home.jsx:264:15">No grants found matching your criteria</p>
              <button
                onClick={() => {
                  setSearchTerm('')
                  setSelectedType('all')
                  setSelectedAudience('all')
                  setMinAmount('')
                  setMaxAmount('')
                }}
                className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
                data-component-path="client/src/pages/Home.jsx" data-component-line="265:15" data-component-id="client/src/pages/Home.jsx:265:15"
              >
                Clear all filters
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  )
}

export default Home